import React, { useRef, useState } from "react";
import { Plus, Trash2, Upload, FileCode, X, Eye, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";

const DEFAULT_XML = { number: "Nummer", doc_type: "Type", date: "Datum", guest_name: "Klant", guest_number: "Klantnr", vat_number: "Btwnr", status: "Status", payment_method: "Betaalwijze", subtotal: "Subtotaal", tax_rate: "BtwTarief", tax_amount: "Btw", total: "Totaal", amount_due: "TeBetalen", currency: "Munt", credit_of_number: "CreditVan", line_code: "Code", line_description: "Omschrijving", line_qty: "Aantal", line_unit_price: "Prijs", line_vat_rate: "BtwTarief", line_total: "RegelTotaal" };

function MapRow({ rows, keys, xsdFields, onChange, t }) {
  const used = rows.map((r) => r.app);
  const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const del = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { app: "", xml: "" }]);
  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 && <p className="text-xs text-muted-foreground italic">{t("xm_no_fields")}</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`xm-row-${i}`}>
          <Select value={r.app} onValueChange={(v) => setRow(i, { app: v, xml: r.xml || DEFAULT_XML[v] || v })}>
            <SelectTrigger className="flex-1" data-testid={`xm-app-${i}`}><SelectValue placeholder={t("xm_pick")} /></SelectTrigger>
            <SelectContent>
              {keys.map((k) => (
                <SelectItem key={k} value={k} disabled={used.includes(k) && r.app !== k}>{t(`accf_${k}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-sm shrink-0">→</span>
          {xsdFields && xsdFields.length ? (
            <Select value={r.xml} onValueChange={(v) => setRow(i, { xml: v })}>
              <SelectTrigger className="flex-1" data-testid={`xm-xml-${i}`}><SelectValue placeholder={t("xm_pick")} /></SelectTrigger>
              <SelectContent>
                {r.xml && !xsdFields.includes(r.xml) && (
                  <SelectItem value={r.xml} className="text-amber-600">{r.xml} ({t("xm_not_in_xsd")})</SelectItem>
                )}
                {xsdFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input className="flex-1 font-mono text-xs" placeholder={t("xm_xml_field")} value={r.xml} onChange={(e) => setRow(i, { xml: e.target.value })} data-testid={`xm-xml-${i}`} />
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => del(i)} data-testid={`xm-del-${i}`}>
            <Trash2 className="h-4 w-4 text-rose-500" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="self-start rounded-full mt-1" data-testid={`xm-add-${keys[0]}`}>
        <Plus className="h-3.5 w-3.5 mr-1" />{t("xm_add_row")}
      </Button>
    </div>
  );
}

export default function XmlMapper({ value, docKeys, lineKeys, onChange, saveAll }) {
  const { t } = useLang();
  const xc = value || {};
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const set = (patch) => onChange({ ...xc, ...patch });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/interfaces/accounting/xsd-parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ xsd_name: data.name, xsd_fields: data.fields });
      toast.success(`${data.fields.length} ${t("xm_xsd_loaded")}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const autofill = () => {
    const doc = docKeys.map((k) => ({ app: k, xml: DEFAULT_XML[k] || k }));
    const line = lineKeys.map((k) => ({ app: k, xml: DEFAULT_XML[k] || k }));
    set({ doc_fields: doc, line_fields: line });
  };

  const showPreview = async () => {
    try {
      if (saveAll) await saveAll();
      const { data } = await api.get("/interfaces/accounting/xml-preview");
      setPreview(data.xml);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-5" data-testid="xml-mapper">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
            <FileCode className="h-4 w-4 text-primary" />{t("xm_title")}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">{t("xm_enable_hint")}</p>
        </div>
        <Switch checked={!!xc.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="xm-enable" />
      </div>

      {xc.enabled && (
        <>
          {/* Bronnen */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">{t("xm_sources")}</p>
            <div className="flex flex-wrap gap-2">
              {[["include_invoices", "xm_src_invoices"], ["include_credits", "xm_src_credits"], ["include_sales", "xm_src_sales"]].map(([k, lbl]) => {
                const on = xc[k] !== false;
                return (
                  <button key={k} type="button" onClick={() => set({ [k]: !on })} data-testid={`xm-${k}`} aria-pressed={on}
                    className={`rounded-full border px-3.5 h-8 text-xs font-medium transition-all active:scale-95 ${on ? "bg-primary text-primary-foreground border-primary shadow-sm" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
                    {t(lbl)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Structuur */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">{t("xm_structure")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["root", "xm_root", "Boekhouding"], ["doc_element", "xm_doc_el", "Document"], ["line_wrapper", "xm_line_wrap", "Regels"], ["line_element", "xm_line_el", "Regel"]].map(([k, lbl, ph]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground">{t(lbl)}</Label>
                  <Input value={xc[k] ?? ""} placeholder={ph} onChange={(e) => set({ [k]: e.target.value })} className="font-mono text-xs" data-testid={`xm-${k}`} />
                </div>
              ))}
            </div>
          </div>

          {/* XSD */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">{t("xm_xsd")}</p>
            <p className="text-xs text-muted-foreground mb-2">{t("xm_xsd_hint")}</p>
            <input ref={fileRef} type="file" accept=".xsd,application/xml,text/xml" onChange={upload} className="hidden" data-testid="xm-xsd-input" />
            {xc.xsd_fields && xc.xsd_fields.length ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-600 px-3 h-8 text-xs font-medium" data-testid="xm-xsd-badge">
                  <FileCode className="h-3.5 w-3.5" />{xc.xsd_name} · {xc.xsd_fields.length} {t("xm_xsd_loaded")}
                </span>
                <Button size="sm" variant="ghost" onClick={() => set({ xsd_name: "", xsd_fields: [] })} data-testid="xm-xsd-clear"><X className="h-3.5 w-3.5 mr-1" />{t("xm_xsd_clear")}</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="xm-xsd-upload"><Upload className="h-3.5 w-3.5 mr-1" />{t("xm_xsd_upload")}</Button>
            )}
          </div>

          <Button size="sm" variant="secondary" onClick={autofill} className="self-start rounded-full" data-testid="xm-autofill"><Wand2 className="h-3.5 w-3.5 mr-1" />{t("xm_autofill")}</Button>

          {/* Mapping */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">{t("xm_doc_fields")}</p>
              <MapRow rows={xc.doc_fields || []} keys={docKeys} xsdFields={xc.xsd_fields} onChange={(r) => set({ doc_fields: r })} t={t} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">{t("xm_line_fields")}</p>
              <MapRow rows={xc.line_fields || []} keys={lineKeys} xsdFields={xc.xsd_fields} onChange={(r) => set({ line_fields: r })} t={t} />
            </div>
          </div>

          <div>
            <Button size="sm" variant="outline" onClick={showPreview} className="rounded-full" data-testid="xm-preview-btn"><Eye className="h-3.5 w-3.5 mr-1" />{t("xm_preview")}</Button>
            {preview && (
              <div className="mt-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1">{t("xm_preview_title")}</p>
                <pre className="rounded-xl border border-border bg-muted/40 p-3 text-[11px] font-mono overflow-auto max-h-80 whitespace-pre" data-testid="xm-preview-out">{preview}</pre>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
